import React, { useState, useEffect } from 'react';
import { Info, Clock, GitBranch, RefreshCw } from 'lucide-react';
import VersionManager from '../utils/versionManager';

interface VersionDisplayProps {
  position?: 'sidebar' | 'footer' | 'header';
  showDetails?: boolean;
}

const VersionDisplay: React.FC<VersionDisplayProps> = ({ 
  position = 'sidebar', 
  showDetails = false 
}) => {
  const [versionInfo, setVersionInfo] = useState(VersionManager.getVersionInfo());
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    // Update version info every minute
    const interval = setInterval(() => {
      setVersionInfo(VersionManager.getVersionInfo());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const handleVersionClick = () => {
    if (position === 'sidebar') {
      setShowTooltip(!showTooltip);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (position === 'footer') {
    return (
      <div className="flex items-center justify-between text-xs text-gray-500 px-6 py-3 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1">
            <GitBranch className="w-3 h-3" />
            <span>{VersionManager.getFormattedVersion()}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Clock className="w-3 h-3" />
            <span>{VersionManager.getLastUpdateInfo()}</span>
          </div>
        </div>
        <div>
          <span>© 2026 Cero Ideas CRM</span>
        </div>
      </div>
    );
  }

  if (position === 'header') {
    return (
      <div className="flex items-center space-x-2 text-xs text-gray-500">
        <GitBranch className="w-3 h-3" />
        <span>{VersionManager.getFormattedVersion()}</span>
        <span>•</span>
        <span>{VersionManager.getBuildInfo()}</span>
      </div>
    );
  }

  // Sidebar version (default)
  return (
    <div className="relative">
      <button
        onClick={handleVersionClick}
        className="w-full text-left p-3 text-xs text-gray-500 hover:bg-gray-50 rounded-lg transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <GitBranch className="w-3 h-3" />
            <span>{VersionManager.getFormattedVersion()}</span>
          </div>
          <Info className="w-3 h-3" />
        </div>
        {showDetails && (
          <div className="mt-1 text-xs text-gray-400">
            {VersionManager.getLastUpdateInfo()}
          </div>
        )}
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute bottom-full left-0 mb-2 w-64 bg-gray-900 text-white text-xs rounded-lg shadow-lg p-3 z-50">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-medium">Versión del Sistema</span>
              <button
                onClick={() => setShowTooltip(false)}
                className="text-gray-400 hover:text-white"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-1">
              <div className="flex justify-between">
                <span>Versión:</span>
                <span className="font-mono">{versionInfo.version}</span>
              </div>
              <div className="flex justify-between">
                <span>Build:</span>
                <span className="font-mono">#{versionInfo.changeCount}</span>
              </div>
              <div className="flex justify-between">
                <span>Compilado:</span>
                <span>{formatDate(versionInfo.buildDate)}</span>
              </div>
              <div className="flex justify-between">
                <span>Actualizado:</span>
                <span>{formatDate(versionInfo.lastUpdate)}</span>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-2 mt-2">
              <p className="text-gray-300">
                La versión se incrementa automáticamente con cada cambio en el sistema.
              </p>
            </div>
          </div>
          
          {/* Arrow */}
          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};

export default VersionDisplay;